(() => {
  // Only change ordinary, one-time lines. Keep gifts, custom properties,
  // discounted products and components of an existing bundle intact.
  function ordinaryLine(line) {
    return !line.selling_plan_allocation && !line.parent_relationship &&
      !Object.keys(line.properties || {}).length &&
      !(line.discount_allocations || []).some(discount => Number(discount.amount) > 0);
  }

  function buildPlan(cart, config) {
    if (!config.available || config.bundlePrice > config.pairPrice) return null;
    const singles = id => cart.items.filter(line =>
      Number(line.variant_id || line.id) === Number(id) && ordinaryLine(line) &&
      !(line.item_components || []).length
    );
    const cleansers = singles(config.cleanser);
    const moisturisers = singles(config.moisturiser);
    const total = lines => lines.reduce((sum, line) => sum + line.quantity, 0);
    const pairs = Math.min(total(cleansers), total(moisturisers));
    if (!pairs) return null;

    const updates = {};
    for (const lines of [cleansers, moisturisers]) {
      let remaining = pairs;
      for (const line of lines) {
        const used = Math.min(line.quantity, remaining);
        if (used) updates[line.key] = line.quantity - used;
        remaining -= used;
      }
    }
    const bundle = cart.items.find(line =>
      Number(line.variant_id || line.id) === Number(config.bundle) && ordinaryLine(line)
    );
    updates[bundle ? bundle.key : String(config.bundle)] = (bundle?.quantity || 0) + pairs;
    return { updates, pairs };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildPlan };
    return;
  }

  const configElement = document.getElementById('ForSkinBundlePairsConfig');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const root = window.Shopify?.routes?.root || '/';
  let pending = Promise.resolve();
  const queue = task => {
    const result = pending.then(task, task);
    pending = result.catch(() => {});
    return result;
  };

  async function jsonRequest(url, options) {
    const response = await fetch(url, options);
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || 'Unable to update your basket. Please try again.');
    }
    return data;
  }

  function showError(message) {
    let notice = document.getElementById('ForSkinBundlePairsError');
    if (!notice) {
      notice = document.createElement('p');
      notice.id = 'ForSkinBundlePairsError';
      notice.setAttribute('role', 'alert');
      notice.className = 'fs-small';
      const container = document.querySelector('.cart-drawer__footer') ||
        document.getElementById('main-cart-footer') || document.querySelector('main');
      container?.prepend(notice);
    }
    notice.textContent = message;
  }

  async function normalise(cart, sections = []) {
    cart = cart || await jsonRequest(`${root}cart.js`);
    const plan = buildPlan(cart, config);
    if (!plan) return null;
    // One request swaps only the matching standalone units for the real
    // Shopify bundle variant; existing bundle quantities and extras remain.
    return jsonRequest(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates: plan.updates,
        sections,
        sections_url: window.location.pathname,
      }),
    });
  }

  window.ForSkinBundles = {
    queue,
    normalise,
    async mutate(url, options, sections = []) {
      return queue(async () => {
        const response = await fetch(url, options);
        const original = await response.json();
        if (!response.ok || original.status || original.errors) return original;
        try {
          const normalised = await normalise(
            typeof original.item_count === 'number' ? original : null,
            sections
          );
          return normalised ? { ...normalised, fs_bundle_pairs_grouped: true } : original;
        } catch (error) {
          // Adding the selected item already succeeded. Do not invite a
          // duplicate add or remove anything else if grouping is unavailable.
          setTimeout(() => showError('Your items are in your basket. We couldn’t group The Double Act yet. Please try checkout again.'), 0);
          console.error('[For Skin] Bundle grouping failed', error);
          return original;
        }
      });
    },
  };

  // A persisted basket may predate this theme update. Group it before the
  // native checkout form is submitted, including after queued quantity edits.
  const resubmitting = new WeakSet();
  document.addEventListener('submit', event => {
    const form = event.target;
    const submitter = event.submitter;
    if (!(form instanceof HTMLFormElement) || submitter?.name !== 'checkout') return;
    if (resubmitting.has(form)) {
      resubmitting.delete(form);
      return;
    }
    event.preventDefault();
    if (form.dataset.fsCheckoutPending === 'true') return;
    form.dataset.fsCheckoutPending = 'true';
    queue(async () => {
      await normalise();
      const liveForm = form.isConnected ? form : document.getElementById(form.id);
      if (!liveForm) throw new Error('Basket form has changed. Please try again.');
      const liveSubmitter = [...document.getElementsByName('checkout')].find(button => button.form === liveForm);
      resubmitting.add(liveForm);
      liveForm.requestSubmit(liveSubmitter);
    }).catch(() => {
      showError('We couldn’t update your basket. Please check your connection and try checkout again.');
    }).finally(() => { delete form.dataset.fsCheckoutPending; });
  }, true);
})();
